/**
 * Communication Panel - Chat + LOG
 * Tabbed panel: CHAT (default) | LOG
 * LOG tab shows unified activity: combat, movement, docking, purchases, etc.
 * Positioned on the right side of the screen with shark-fin background.
 * Matches Figma design: 243x508, #131026 bg, cyan 15% border, Orbitron tabs.
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import {
  Colors, CSSColors, Fonts, Depth, drawSharkFinRight,
} from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

// ── Panel dimensions (Figma) ──────────────────────────────────
const PANEL_WIDTH  = 243;
const PANEL_HEIGHT = 508;

// Figma layout offsets (relative to panel top-left)
const TITLE_Y      = 61;
const TAB_Y        = 111;
const TAB_H        = 28;
const CONTENT_Y    = 181;
const CONTENT_H    = 226;
const INPUT_Y      = 442;
const INPUT_H      = 39;
const INPUT_W      = 202;
const INPUT_X      = 20;            // centered: (243 - 202) / 2 ≈ 20
const MSG_X        = 25;            // centered: (243 - 192) / 2 ≈ 25
const MSG_W        = 192;

// Figma colors
const BG_COLOR           = 0x131026;
const BORDER_CYAN        = 0x00ffff;
const TITLE_COLOR        = '#E0E8FF';
const TAB_ACTIVE_COLOR   = '#ffffff';
const TAB_INACTIVE_COLOR = '#AABBDD';
const MSG_BODY_COLOR     = '#AABBDD';
const INPUT_BG           = 0x03030C;
const INPUT_PLACEHOLDER  = '#AABBDD';

const MAX_LOG_ENTRIES = 60;
const MAX_CHAT_MESSAGES = 200;

// Username color rotation for other players
const USERNAME_COLORS = ['#FFFF00', '#FF00FF', '#00FFFF', '#FF8800', '#88FF00'];

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: number;
  type: 'alliance' | 'global' | 'whisper' | 'system';
}

interface LogEntry {
  timestamp: number;
  text: string;
  color: string;
}

type ActiveTab = 'chat' | 'log';

export class EnhancedChatPanel extends Phaser.GameObjects.Container {
  private mmoState: MMOStateManager;
  private mmoNetwork: MMONetworkManager;

  // Panel chrome
  private panelBg: Phaser.GameObjects.Graphics;
  private clipShape!: Phaser.GameObjects.Graphics;

  // Tabs
  private tabContainer!: Phaser.GameObjects.Container;
  private activeTab: ActiveTab = 'chat';
  private chatUnread = 0;
  private logUnread = 0;

  // Chat content
  private chatContentContainer!: Phaser.GameObjects.Container;
  private chatMessages: ChatMessage[] = [];
  private chatScrollOffset = 0;
  private chatMaxScroll = 0;

  // LOG content
  private logContentContainer!: Phaser.GameObjects.Container;
  private logEntries: LogEntry[] = [];
  private logScrollOffset = 0;
  private logMaxScroll = 0;

  // Chat channel sub-tabs (WoW style)
  private channelBarContainer?: Phaser.GameObjects.Container;
  private chatChannel: 'alliance' | 'global' = 'global';

  // Chat input
  private inputContainer!: Phaser.GameObjects.Container;
  private inputBg!: Phaser.GameObjects.Graphics;
  private inputText!: Phaser.GameObjects.Text;
  private inputCursor!: Phaser.GameObjects.Graphics;
  private inputHitZone!: Phaser.GameObjects.Zone;
  private sendIcon!: Phaser.GameObjects.Image;
  private sendHitZone!: Phaser.GameObjects.Zone;
  private cursorBlinkTimer?: Phaser.Time.TimerEvent;
  private currentInputHeight = INPUT_H;
  private currentInput = '';
  private isInputFocused = false;
  private hiddenInput!: HTMLInputElement;  // hidden DOM input for emoji/IME/paste

  // Username color cache
  private userColorMap: Map<string, string> = new Map();
  private colorIndex = 0;

  // Mobile strip mode
  private mobileMode: boolean = false;
  private mobileCollapsed: boolean = true;
  private mobileToggleBtn: Phaser.GameObjects.Container | null = null;
  private mobileLatestMsg: Phaser.GameObjects.Text | null = null;

  // Soft-keyboard lift (mobile only): when the iOS/Android on-screen
  // keyboard appears the visual viewport shrinks but the Phaser canvas
  // stays full-height — so the chat input ends up hidden behind the
  // keyboard. We listen to `visualViewport.resize` while the input is
  // focused and translate the panel up by the keyboard height so the
  // input stays visible.
  private yBeforeKeyboardLift: number | null = null;
  private viewportResizeHandler: (() => void) | null = null;

  // Desktop collapse state
  private desktopCollapsed = false;
  private expandedX = 0; // final resting x when expanded
  private collapseArrow!: Phaser.GameObjects.Text;
  private collapseHitZone!: Phaser.GameObjects.Zone;

  constructor(scene: Phaser.Scene, x: number, y: number, mmoState: MMOStateManager, mmoNetwork: MMONetworkManager) {
    super(scene, x, y);

    this.mmoState = mmoState;
    this.mmoNetwork = mmoNetwork;
    this.mobileMode = isMobile(scene);

    if (this.mobileMode) {
      // ── Mobile chat strip: full-width collapsed bar ──
      const screenW = scene.cameras.main.width;
      this.panelBg = scene.add.graphics();
      this.drawMobileBackground(screenW);
      this.add(this.panelBg);

      // Clip mask
      this.clipShape = new Phaser.GameObjects.Graphics(scene);
      this.updateClipMask();
      const clipMask = new Phaser.Display.Masks.GeometryMask(scene, this.clipShape);

      // Chat content (clipped)
      this.chatContentContainer = scene.add.container(0, 40);
      this.chatContentContainer.setMask(clipMask);
      this.chatContentContainer.setVisible(false);
      this.add(this.chatContentContainer);

      // Log content (hidden)
      this.logContentContainer = scene.add.container(0, 40);
      this.logContentContainer.setMask(clipMask);
      this.logContentContainer.setVisible(false);
      this.add(this.logContentContainer);

      // Tab container (hidden in collapsed)
      this.tabContainer = scene.add.container(0, 0);
      this.add(this.tabContainer);

      // Latest message preview
      this.mobileLatestMsg = scene.add.text(40, 10, I18n.t('chat.tab_chat'), {
        fontSize: '11px', color: '#AABBDD', fontFamily: Fonts.body,
        wordWrap: { width: screenW - 90 },
      });
      this.mobileLatestMsg.setOrigin(0, 0);
      this.add(this.mobileLatestMsg);

      // Toggle button (expand/collapse)
      this.mobileToggleBtn = scene.add.container(8, 8);
      const toggleBg = scene.add.graphics();
      toggleBg.fillStyle(0x00ffff, 0.3);
      toggleBg.fillRoundedRect(0, 0, 28, 24, 4);
      this.mobileToggleBtn.add(toggleBg);
      const toggleIcon = scene.add.text(14, 12, '\u25B2', { fontSize: '12px', color: '#00ffff' });
      toggleIcon.setOrigin(0.5);
      this.mobileToggleBtn.add(toggleIcon);
      const toggleHit = scene.add.zone(14, 12, 28, 24).setInteractive({ useHandCursor: true });
      toggleHit.on('pointerdown', () => this.toggleMobileChat());
      this.mobileToggleBtn.add(toggleHit);
      this.add(this.mobileToggleBtn);

      // Input area
      this.createInputArea();

      this.setupScrolling();
      this.buildChannelBar();

      // Start collapsed
      this.setMobileCollapsed(true);
    } else {
      // ── Desktop: full shark-fin panel ──
      this.panelBg = scene.add.graphics();
      this.drawBackground();
      this.add(this.panelBg);

      const title = scene.add.text(PANEL_WIDTH - 23, TITLE_Y, I18n.t('chat.title'), {
        fontSize: '16px',
        color: TITLE_COLOR,
        fontFamily: Fonts.heading,
        fontStyle: '900',
        letterSpacing: 2,
        lineSpacing: 25.5,
      });
      title.setOrigin(1, 0);
      this.add(title);

      this.tabContainer = scene.add.container(0, TAB_Y);
      this.add(this.tabContainer);
      this.buildTabs();

      this.clipShape = new Phaser.GameObjects.Graphics(scene);
      this.updateClipMask();
      const clipMask = new Phaser.Display.Masks.GeometryMask(scene, this.clipShape);

      this.chatContentContainer = scene.add.container(0, CONTENT_Y);
      this.chatContentContainer.setMask(clipMask);
      this.add(this.chatContentContainer);

      this.logContentContainer = scene.add.container(0, CONTENT_Y);
      this.logContentContainer.setMask(clipMask);
      this.logContentContainer.setVisible(false);
      this.add(this.logContentContainer);

      this.createInputArea();
      this.setupScrolling();
      this.buildChannelBar();
      this.buildCollapseToggle();
    }

    // ── Events ──
    EventBus.on(MMOEvents.ALLIANCE_CHAT, this.onAllianceChatMessage, this);
    EventBus.on(MMOEvents.GLOBAL_CHAT, this.onGlobalChatMessage, this);
    EventBus.on('mmo-typing', this.onUserTyping, this);
    // Listen for alliance changes to rebuild channel bar
    EventBus.on('mmo-alliance-updated', this.onAllianceChanged, this);
    EventBus.on(MMOEvents.WORLD_STATE, this.onWorldState, this);

    // ── LOG event listeners (unified activity) ──
    EventBus.on(MMOEvents.COMBAT_RESULT, this.onLogCombatResult, this);
    EventBus.on(MMOEvents.ENGAGEMENT_STARTED, this.onLogEngagement, this);
    EventBus.on(MMOEvents.ENGAGEMENT_ENDED, this.onLogEngagementEnd, this);
    EventBus.on(MMOEvents.UNIT_MOVED, this.onLogUnitMoved, this);
    EventBus.on('mmo-unit-docked', this.onLogUnitDocked, this);
    EventBus.on('mmo-unit-undocked', this.onLogUnitUndocked, this);
    EventBus.on(MMOEvents.BUILD_QUEUED, this.onLogBuildQueued, this);
    EventBus.on(MMOEvents.BUILD_COMPLETE, this.onLogBuildComplete, this);
    EventBus.on(MMOEvents.PURCHASE_CONFIRMED, this.onLogPurchase, this);
    EventBus.on(MMOEvents.ABILITY_ACTIVATED, this.onLogAbility, this);
    EventBus.on(MMOEvents.MINE_RESULT, this.onLogMineResult, this);
    EventBus.on('mmo-log-cargo-unloaded', this.onLogCargoUnloaded, this);
    EventBus.on(MMOEvents.TRANSFER_COMPLETE, this.onLogTransferComplete, this);
    EventBus.on(MMOEvents.TRANSFER_STARTED, this.onLogTransferStarted, this);
    EventBus.on(MMOEvents.TRANSFER_CANCELLED, this.onLogTransferCancelled, this);
    EventBus.on(MMOEvents.MINERALS_DEPOSITED, this.onLogMineralsDeposited, this);
    EventBus.on(MMOEvents.MINERALS_WITHDRAWN, this.onLogMineralsWithdrawn, this);
    EventBus.on(MMOEvents.CARGO_LOADED, this.onLogCargoLoaded, this);
    EventBus.on(MMOEvents.STATION_WRECK_CREATED, this.onLogStationWreckCreated, this);
    EventBus.on(MMOEvents.STATION_WRECK_SCAVENGED, this.onLogStationWreckScavenged, this);
    EventBus.on(MMOEvents.STATION_REPAIRED, this.onLogStationRepaired, this);

    // Initial log entry
    this.addLogEntry(I18n.t('chat.log_initialized'), '#667799');

    this.setDepth(Depth.panel);
    scene.add.existing(this);
  }

  // ── Desktop Collapse / Expand ─────────────────────────────

  private buildCollapseToggle(): void {
    const arrowX = -18;
    const arrowY = PANEL_HEIGHT / 2;

    // Arrow text: ▶ when expanded (click to collapse right), ◀ when collapsed (click to expand left)
    this.collapseArrow = this.scene.add.text(arrowX, arrowY, '▶', {
      fontSize: '16px',
      color: '#00ffff',
      fontFamily: Fonts.heading,
      fontStyle: '900',
    });
    this.collapseArrow.setOrigin(1, 0.5);
    this.collapseArrow.setAlpha(0.5);
    this.add(this.collapseArrow);

    // Hit zone for the arrow
    this.collapseHitZone = this.scene.add.zone(arrowX - 22, arrowY - 16, 28, 32).setOrigin(0, 0);
    this.collapseHitZone.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, 28, 32),
      Phaser.Geom.Rectangle.Contains,
    );
    this.add(this.collapseHitZone);

    this.collapseHitZone.on('pointerover', () => {
      this.collapseArrow.setAlpha(1);
      this.scene.input.setDefaultCursor('pointer');
    });
    this.collapseHitZone.on('pointerout', () => {
      this.collapseArrow.setAlpha(0.5);
      this.scene.input.setDefaultCursor('default');
    });
    this.collapseHitZone.on('pointerdown', () => {
      this.toggleDesktopCollapse();
    });
  }

  private toggleDesktopCollapse(): void {
    this.desktopCollapsed = !this.desktopCollapsed;
    const screenW = this.scene.cameras.main.width;

    if (this.desktopCollapsed) {
      this.collapseArrow.setText('◀');
      // Slide right so only the arrow remains visible at the screen edge
      this.scene.tweens.add({
        targets: this,
        x: screenW + 8,
        duration: 300,
        ease: 'Power2',
        onUpdate: () => this.updateClipMask(),
        onComplete: () => this.updateClipMask(),
      });
    } else {
      this.collapseArrow.setText('▶');
      this.scene.tweens.add({
        targets: this,
        x: this.expandedX,
        duration: 300,
        ease: 'Power2',
        onUpdate: () => this.updateClipMask(),
        onComplete: () => this.updateClipMask(),
      });
    }
  }

  /** Called after the initial slide-in tween to record final x */
  public setExpandedX(x: number): void {
    this.expandedX = x;
  }

  // ── Drawing ───────────────────────────────────────────────

  private drawBackground(): void {
    this.panelBg.clear();
    drawSharkFinRight(this.panelBg, 0, 0, PANEL_WIDTH, PANEL_HEIGHT, {
      fillColor: BG_COLOR,
      fillAlpha: 1.0,
      borderColor: BORDER_CYAN,
      borderAlpha: 0.15,
    });
  }

  private drawMobileBackground(w: number, h: number = 40): void {
    this.panelBg.clear();
    this.panelBg.fillStyle(BG_COLOR, 0.95);
    this.panelBg.fillRect(0, 0, w, h);
    this.panelBg.lineStyle(1, BORDER_CYAN, 0.2);
    this.panelBg.lineBetween(0, 0, w, 0);
  }

  private toggleMobileChat(): void {
    this.setMobileCollapsed(!this.mobileCollapsed);
  }

  private setMobileCollapsed(collapsed: boolean): void {
    this.mobileCollapsed = collapsed;
    const screenW = this.scene.cameras.main.width;
    const screenH = this.scene.cameras.main.height;

    if (collapsed) {
      // 40px strip: toggle + latest msg
      this.drawMobileBackground(screenW, 40);
      this.chatContentContainer.setVisible(false);
      this.logContentContainer.setVisible(false);
      this.inputContainer?.setVisible(false);
      this.channelBarContainer?.setVisible(false);
      this.tabContainer?.setVisible(false);
      this.mobileLatestMsg?.setVisible(true);
      this.setY(screenH - 40);
    } else {
      // 160px expanded: messages + input
      const expandedH = 160;
      this.drawMobileBackground(screenW, expandedH);
      this.chatContentContainer.setVisible(true);
      this.inputContainer?.setVisible(true);
      this.channelBarContainer?.setVisible(true);
      this.tabContainer?.setVisible(false);
      this.mobileLatestMsg?.setVisible(false);
      this.setY(screenH - expandedH);
      // Reposition input at bottom of expanded area
      this.inputContainer?.setPosition(8, expandedH - INPUT_H - 4);
      // Update clip mask for expanded content
      this.updateClipMask();
      this.refreshChatDisplay();
    }
  }

  /** Redraw the clip mask at the panel's current world position. */
  public updateClipMask(): void {
    if (!this.clipShape) return;
    this.clipShape.clear();
    if (this.mobileMode) {
      const screenW = this.scene.cameras.main.width;
      this.clipShape.fillRect(this.x, this.y + 40, screenW, 80);
    } else {
      this.clipShape.fillRect(this.x, this.y + CONTENT_Y, PANEL_WIDTH, CONTENT_H);
    }
  }

  // ── Tabs ──────────────────────────────────────────────────

  private buildTabs(): void {
    this.tabContainer.removeAll(true);

    const CHAT_LEFT = 52;
    const LOG_RIGHT = 42; // inset from curved shark-fin edge

    // CHAT label
    const chatText = this.scene.add.text(CHAT_LEFT, TAB_H / 2, I18n.t('chat.tab_chat'), {
      fontSize: '10px',
      color: this.activeTab === 'chat' ? TAB_ACTIVE_COLOR : TAB_INACTIVE_COLOR,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
    });
    chatText.setOrigin(0, 0.5);
    this.tabContainer.add(chatText);

    // LOG label
    const logText = this.scene.add.text(PANEL_WIDTH - LOG_RIGHT, TAB_H / 2, I18n.t('chat.tab_log'), {
      fontSize: '10px',
      color: this.activeTab === 'log' ? TAB_ACTIVE_COLOR : TAB_INACTIVE_COLOR,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
    });
    logText.setOrigin(1, 0.5);
    this.tabContainer.add(logText);

    // Unread badges
    if (this.chatUnread > 0 && this.activeTab !== 'chat') {
      const chatRight = CHAT_LEFT + chatText.width;
      const badge = this.scene.add.graphics();
      badge.fillStyle(Colors.red, 0.9);
      badge.fillCircle(chatRight + 10, 8, 7);
      this.tabContainer.add(badge);
      const badgeLabel = this.scene.add.text(chatRight + 10, 8, this.chatUnread > 9 ? '9+' : this.chatUnread.toString(), {
        fontSize: '8px', color: CSSColors.textWhite, fontFamily: Fonts.body,
      });
      badgeLabel.setOrigin(0.5);
      this.tabContainer.add(badgeLabel);
    }
    if (this.logUnread > 0 && this.activeTab !== 'log') {
      const badge = this.scene.add.graphics();
      badge.fillStyle(Colors.red, 0.9);
      badge.fillCircle(PANEL_WIDTH - LOG_RIGHT + 10, 8, 7);
      this.tabContainer.add(badge);
      const badgeLabel = this.scene.add.text(PANEL_WIDTH - LOG_RIGHT + 10, 8, this.logUnread > 9 ? '9+' : this.logUnread.toString(), {
        fontSize: '8px', color: CSSColors.textWhite, fontFamily: Fonts.body,
      });
      badgeLabel.setOrigin(0.5);
      this.tabContainer.add(badgeLabel);
    }

    // Full-width separator line (below tabs)
    const sepGfx = this.scene.add.graphics();
    sepGfx.lineStyle(1, BORDER_CYAN, 0.15);
    sepGfx.lineBetween(2, TAB_H, PANEL_WIDTH - 2, TAB_H);
    this.tabContainer.add(sepGfx);

    // Active tab indicator (bright cyan, 3px) — under the active tab text
    const indicatorGfx = this.scene.add.graphics();
    indicatorGfx.lineStyle(3, BORDER_CYAN, 0.85);
    if (this.activeTab === 'chat') {
      const chatRight = CHAT_LEFT + chatText.width;
      indicatorGfx.lineBetween(CHAT_LEFT - 8, TAB_H, chatRight + 8, TAB_H);
    } else {
      const logLeft = PANEL_WIDTH - LOG_RIGHT - logText.width;
      indicatorGfx.lineBetween(logLeft - 8, TAB_H, PANEL_WIDTH - LOG_RIGHT + 8, TAB_H);
    }
    this.tabContainer.add(indicatorGfx);

    // Hit zones for each tab
    const midPoint = PANEL_WIDTH / 2;
    const chatHit = this.scene.add.zone(0, 0, midPoint, TAB_H).setOrigin(0, 0);
    chatHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, midPoint, TAB_H), Phaser.Geom.Rectangle.Contains);
    chatHit.on('pointerdown', () => this.switchTab('chat'));
    chatHit.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
    chatHit.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
    this.tabContainer.add(chatHit);

    const logHit = this.scene.add.zone(midPoint, 0, midPoint, TAB_H).setOrigin(0, 0);
    logHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, midPoint, TAB_H), Phaser.Geom.Rectangle.Contains);
    logHit.on('pointerdown', () => this.switchTab('log'));
    logHit.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
    logHit.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
    this.tabContainer.add(logHit);
  }

  private switchTab(tab: ActiveTab): void {
    if (tab === this.activeTab) return;
    this.activeTab = tab;

    if (tab === 'chat') {
      this.chatUnread = 0;
      this.chatContentContainer.setVisible(true);
      this.logContentContainer.setVisible(false);
      this.inputContainer.setVisible(true);
    } else {
      this.logUnread = 0;
      this.chatContentContainer.setVisible(false);
      this.logContentContainer.setVisible(true);
      this.inputContainer.setVisible(false);
    }

    this.buildTabs();
    this.buildChannelBar();
  }

  // ── Channel sub-tabs (Alliance / Global) ────────────────────

  private buildChannelBar(): void {
    // Destroy previous bar
    if (this.channelBarContainer) {
      this.channelBarContainer.destroy(true);
      this.channelBarContainer = undefined;
    }

    // Only show when CHAT tab is active and player is in an alliance
    const hasAlliance = !!this.mmoState.getAlliance();
    if (this.activeTab !== 'chat') return;

    // If not in alliance, force global channel and skip the bar
    if (!hasAlliance) {
      this.chatChannel = 'global';
      // Shift chat content back to normal position
      this.chatContentContainer.setY(CONTENT_Y);
      return;
    }

    const BAR_Y = CONTENT_Y - 30; // sits between main tabs and chat content
    const BAR_H = 24;

    this.channelBarContainer = this.scene.add.container(0, BAR_Y);
    this.add(this.channelBarContainer);

    // Subtle background
    const barBg = this.scene.add.graphics();
    barBg.fillStyle(0x0a0e1e, 0.6);
    barBg.fillRect(8, 0, PANEL_WIDTH - 16, BAR_H);
    this.channelBarContainer.add(barBg);

    // Channel options
    const channels: { id: 'alliance' | 'global'; label: string; x: number }[] = [
      { id: 'alliance', label: I18n.t('chat.channel_alliance'), x: PANEL_WIDTH * 0.30 },
      { id: 'global', label: I18n.t('chat.channel_global'), x: PANEL_WIDTH * 0.70 },
    ];

    channels.forEach(ch => {
      const isActive = ch.id === this.chatChannel;

      const label = this.scene.add.text(ch.x, BAR_H / 2, ch.label, {
        fontSize: '9px',
        fontStyle: '900',
        color: isActive ? '#FFFFFF' : '#556688',
        fontFamily: Fonts.heading,
        letterSpacing: 2,
      });
      label.setOrigin(0.5, 0.5);
      this.channelBarContainer!.add(label);

      // Active indicator dot
      if (isActive) {
        const dot = this.scene.add.circle(ch.x - label.width / 2 - 8, BAR_H / 2, 3, ch.id === 'alliance' ? 0xFDB15B : 0x00FFFF);
        this.channelBarContainer!.add(dot);
      }

      // Hit zone
      const hitW = 80;
      const hit = this.scene.add.zone(ch.x - hitW / 2, 0, hitW, BAR_H).setOrigin(0, 0);
      hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, hitW, BAR_H), Phaser.Geom.Rectangle.Contains);
      hit.on('pointerover', () => {
        if (!isActive) label.setColor('#AABBCC');
        this.scene.input.setDefaultCursor('pointer');
      });
      hit.on('pointerout', () => {
        if (!isActive) label.setColor('#556688');
        this.scene.input.setDefaultCursor('default');
      });
      hit.on('pointerdown', () => {
        if (ch.id !== this.chatChannel) {
          this.chatChannel = ch.id;
          this.buildChannelBar();
          this.refreshChatDisplay();
          this.chatScrollToBottom();
        }
      });
      this.channelBarContainer!.add(hit);
    });

    // Push chat content down a bit to make room for channel bar
    this.chatContentContainer.setY(CONTENT_Y + 2);
  }

  // ── Chat input ────────────────────────────────────────────

  private createInputArea(): void {
    const textAreaW = INPUT_W - 50; // leave room for send button

    this.inputContainer = this.scene.add.container(INPUT_X, INPUT_Y);
    this.add(this.inputContainer);

    // Background (redrawn when height changes)
    this.inputBg = this.scene.add.graphics();
    this.drawInputBg(INPUT_H);
    this.inputContainer.add(this.inputBg);

    // Placeholder / input text — word-wrapped within the text area
    this.inputText = this.scene.add.text(12, 0, this.chatPlaceholder, {
      fontSize: '13px',
      color: INPUT_PLACEHOLDER,
      fontFamily: Fonts.body,
      letterSpacing: 1,
      wordWrap: { width: textAreaW },
      lineSpacing: 2,
    });
    this.inputText.setOrigin(0, 0);
    this.centerTextY(INPUT_H);
    this.inputContainer.add(this.inputText);

    // Blinking cursor line (hidden by default)
    this.inputCursor = this.scene.add.graphics();
    this.inputCursor.lineStyle(1.5, 0xffffff, 1);
    this.inputCursor.lineBetween(0, 0, 0, 14);
    this.inputCursor.setVisible(false);
    this.inputContainer.add(this.inputCursor);

    // Send button icon — loaded from PNG asset (anchored to right)
    const sendX = INPUT_W - 22;
    const sendY = INPUT_H / 2;
    this.sendIcon = this.scene.add.image(sendX, sendY, 'icon-send');
    this.sendIcon.setDisplaySize(26, 26);
    this.sendIcon.setOrigin(0.5, 0.5);
    this.inputContainer.add(this.sendIcon);

    // Send button hit zone
    const sendHit = this.scene.add.zone(sendX - 13, sendY - 13, 26, 26).setOrigin(0, 0);
    sendHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 26, 26), Phaser.Geom.Rectangle.Contains);
    this.inputContainer.add(sendHit);
    this.sendHitZone = sendHit;
    sendHit.on('pointerdown', () => this.sendMessage());
    sendHit.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
    sendHit.on('pointerout', () => this.scene.input.setDefaultCursor('default'));

    // Input hit zone (full input area for focusing)
    this.inputHitZone = this.scene.add.zone(0, 0, INPUT_W, INPUT_H).setOrigin(0, 0);
    this.inputHitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, INPUT_W, INPUT_H), Phaser.Geom.Rectangle.Contains);
    this.inputContainer.add(this.inputHitZone);
    this.inputHitZone.on('pointerdown', () => this.focusInput());

    // Hidden DOM input for emoji/IME/paste support
    this.hiddenInput = document.createElement('input');
    this.hiddenInput.type = 'text';
    this.hiddenInput.maxLength = 200;
    this.hiddenInput.autocomplete = 'off';
    Object.assign(this.hiddenInput.style, {
      position: 'fixed',
      left: '-9999px',
      top: '-9999px',
      width: '1px',
      height: '1px',
      opacity: '0',
      pointerEvents: 'none',
    });
    document.body.appendChild(this.hiddenInput);

    // Sync DOM input → Phaser text (catches emojis, paste, IME)
    this.hiddenInput.addEventListener('input', () => {
      this.currentInput = this.hiddenInput.value;
      this.syncInputDisplay();
    });

    this.hiddenInput.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter') { event.preventDefault(); this.sendMessage(); }
      else if (event.key === 'Escape') { event.preventDefault(); this.unfocusInput(); }
    });

    // Mobile users typically dismiss the keyboard via the device's Done /
    // back-gesture rather than Escape — without a blur listener the chat
    // panel would stay lifted above an absent keyboard.
    this.hiddenInput.addEventListener('blur', () => {
      if (this.isInputFocused) this.unfocusInput();
    });

    // Re-focus hidden input if user clicks the Phaser canvas while typing
    this.scene.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      if (this.isInputFocused) {
        // Keep hidden input focused for emoji picker
        if (document.activeElement !== this.hiddenInput) {
          this.hiddenInput.focus();
        }
        // Prevent game shortcuts
        event.stopPropagation();
      }
    });
  }

  private drawInputBg(h: number): void {
    this.inputBg.clear();
    this.inputBg.fillStyle(INPUT_BG, 1.0);
    this.inputBg.fillRect(0, 0, INPUT_W, h);
  }

  /** Center text vertically in the current box height */
  private centerTextY(boxH: number): void {
    const textH = this.inputText.height;
    this.inputText.setY(Math.floor((boxH - textH) / 2));
  }

  /** Position the send button at the vertical center of the box */
  private positionButtons(boxH: number): void {
    const cy = boxH / 2;
    this.sendIcon.setY(cy);
    this.sendHitZone.setY(cy - 13);
  }

  /** Recalculate input box height based on text content, grow upward */
  private resizeInputBox(): void {
    const textH = this.inputText.height;
    const padding = 12;
    const newH = Math.max(INPUT_H, textH + padding);

    if (newH !== this.currentInputHeight) {
      this.currentInputHeight = newH;
      this.drawInputBg(newH);
      this.inputContainer.setY(INPUT_Y + INPUT_H - newH);
      this.inputHitZone.setSize(INPUT_W, newH);
      this.inputHitZone.input!.hitArea = new Phaser.Geom.Rectangle(0, 0, INPUT_W, newH);
      this.positionButtons(newH);
    }

    this.centerTextY(newH);
  }

  /** Placeholder text for the chat input */
  private get chatPlaceholder(): string {
    return I18n.t('chat.placeholder');
  }

  /** Sync Phaser display from currentInput (called after any input change) */
  private syncInputDisplay(): void {
    this.inputText.setText(this.currentInput || this.chatPlaceholder);
    if (!this.currentInput) {
      this.inputText.setColor(INPUT_PLACEHOLDER);
      this.currentInputHeight = INPUT_H;
      this.drawInputBg(INPUT_H);
      this.inputContainer.setY(INPUT_Y);
      this.centerTextY(INPUT_H);
      this.positionButtons(INPUT_H);
    } else {
      this.inputText.setColor(CSSColors.textWhite);
      this.resizeInputBox();
    }
    this.updateCursorPosition();
  }

  // Emoji picker removed — users can still use Win+. or Cmd+Ctrl+Space while typing

  private focusInput(): void {
    this.isInputFocused = true;
    this.hiddenInput.value = this.currentInput;
    this.hiddenInput.focus();
    if (!this.currentInput) this.inputText.setText('');
    this.inputText.setColor(CSSColors.textWhite);
    this.showCursor();
    this.attachKeyboardLift();
    EventBus.emit('chat-input-focused');
  }

  private unfocusInput(): void {
    this.isInputFocused = false;
    this.hiddenInput.blur();
    if (!this.currentInput) {
      this.inputText.setText(this.chatPlaceholder);
      this.inputText.setColor(INPUT_PLACEHOLDER);
    }
    this.hideCursor();
    this.detachKeyboardLift();
    EventBus.emit('chat-input-unfocused');
  }

  // ── Soft-keyboard lift (mobile) ────────────────────────────
  /**
   * While the on-screen keyboard is up the visual viewport's height
   * shrinks but the Phaser canvas (and therefore the chat panel's
   * native coordinate space) stays full-size. Without this lift the
   * chat input row sits underneath the keyboard and the user types
   * blind. We translate the panel up by the keyboard height so the
   * input always stays just above the keyboard.
   */
  private attachKeyboardLift(): void {
    if (!this.mobileMode) return;
    const vv = (window as Window & { visualViewport?: VisualViewport }).visualViewport;
    if (!vv) return;
    if (this.viewportResizeHandler) return; // already attached
    this.yBeforeKeyboardLift = this.y;

    const apply = (): void => {
      if (!this.isInputFocused) return;
      const layoutH = this.scene.cameras.main.height;
      const visibleH = vv.height + vv.offsetTop;
      const keyboardH = Math.max(0, layoutH - visibleH);
      if (keyboardH <= 0) {
        // No keyboard yet (focus may fire before resize). Land at default.
        if (this.yBeforeKeyboardLift !== null) this.setY(this.yBeforeKeyboardLift);
        this.updateClipMask();
        return;
      }
      // Estimate the panel's rendered height. In mobile-expanded mode the
      // panel is 160px; in mobile-collapsed mode it's 40px. Fall back to
      // 160 if we're not sure, so the input lands above the keyboard.
      const panelH = this.mobileCollapsed ? 40 : 160;
      const targetY = layoutH - keyboardH - panelH - 4;
      this.setY(Math.max(0, targetY));
      this.updateClipMask();
    };

    this.viewportResizeHandler = apply;
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    // Run once now in case the keyboard is already up by the time focus fires.
    // iOS often raises the keyboard as part of the same task that triggers
    // focus, so apply() needs to run on the next frame to see the resized
    // visualViewport.
    requestAnimationFrame(apply);
  }

  private detachKeyboardLift(): void {
    if (!this.mobileMode) return;
    const vv = (window as Window & { visualViewport?: VisualViewport }).visualViewport;
    if (vv && this.viewportResizeHandler) {
      vv.removeEventListener('resize', this.viewportResizeHandler);
      vv.removeEventListener('scroll', this.viewportResizeHandler);
    }
    this.viewportResizeHandler = null;
    if (this.yBeforeKeyboardLift !== null) {
      this.setY(this.yBeforeKeyboardLift);
      this.updateClipMask();
      this.yBeforeKeyboardLift = null;
    }
  }

  private showCursor(): void {
    this.updateCursorPosition();
    this.inputCursor.setVisible(true);
    // Standard text cursor blink: visible 500ms, hidden 500ms
    this.cursorBlinkTimer?.destroy();
    this.cursorBlinkTimer = this.scene.time.addEvent({
      delay: 500,
      loop: true,
      callback: () => {
        this.inputCursor.setVisible(!this.inputCursor.visible);
      },
    });
  }

  private hideCursor(): void {
    this.cursorBlinkTimer?.destroy();
    this.cursorBlinkTimer = undefined;
    this.inputCursor.setVisible(false);
  }

  private updateCursorPosition(): void {
    if (!this.currentInput) {
      // Place cursor at the start
      this.inputCursor.setPosition(12, this.inputText.y);
      return;
    }
    // For wrapped text, find the position at the end of the last line
    const lines = this.inputText.getWrappedText(this.currentInput);
    const lastLine = lines[lines.length - 1] || '';
    // Measure the last line width using a temp text
    const tempText = this.scene.add.text(0, 0, lastLine, {
      fontSize: '13px', fontFamily: Fonts.body, letterSpacing: 1,
    });
    const lastLineW = tempText.width;
    tempText.destroy();

    const lineH = this.inputText.lineSpacing + 13 + 2; // fontSize + lineSpacing approx
    const cursorX = 12 + lastLineW;
    const cursorY = this.inputText.y + (lines.length - 1) * lineH;
    this.inputCursor.setPosition(cursorX, cursorY);
  }

  private sendMessage(): void {
    if (!this.currentInput.trim()) return;
    const msg = this.currentInput.trim();
    this.currentInput = '';
    this.hiddenInput.value = '';
    this.inputText.setText(this.chatPlaceholder);
    this.inputText.setColor(INPUT_PLACEHOLDER);
    // Reset box to default height
    this.currentInputHeight = INPUT_H;
    this.drawInputBg(INPUT_H);
    this.inputContainer.setY(INPUT_Y);
    this.centerTextY(INPUT_H);
    this.positionButtons(INPUT_H);
    this.unfocusInput();

    this.chatMessages.push({
      id: `${Date.now()}-${Math.random()}`,
      senderId: this.mmoState.getUserId() || 'local',
      senderName: 'Me',
      message: msg,
      timestamp: Date.now(),
      type: this.chatChannel,
    });
    if (this.chatMessages.length > MAX_CHAT_MESSAGES) this.chatMessages.splice(0, this.chatMessages.length - MAX_CHAT_MESSAGES);
    this.refreshChatDisplay();
    this.chatScrollToBottom();

    if (this.chatChannel === 'alliance') {
      this.mmoNetwork.sendAllianceChat(msg);
    } else {
      this.mmoNetwork.sendGlobalChat(msg);
    }
  }

  // ── Username color assignment ─────────────────────────────

  private getUsernameColor(senderId: string): string {
    const myId = this.mmoState.getUserId();
    if (senderId === myId || senderId === 'local') return '#FFFFFF'; // white for self

    if (!this.userColorMap.has(senderId)) {
      this.userColorMap.set(senderId, USERNAME_COLORS[this.colorIndex % USERNAME_COLORS.length]);
      this.colorIndex++;
    }
    return this.userColorMap.get(senderId)!;
  }

  // ── Chat display ──────────────────────────────────────────

  private refreshChatDisplay(): void {
    this.chatContentContainer.removeAll(true);

    let y = 0;
    const filtered = this.chatMessages.filter((m) => m.type === this.chatChannel || m.type === 'system');

    // Loading state (d14)
    if (filtered.length === 0) {
      const emptyText = this.scene.add.text(MSG_X, y + 20, I18n.t('chat.no_messages'), {
        fontSize: '13px', color: '#445566', fontFamily: Fonts.body, lineSpacing: 4,
      });
      this.chatContentContainer.add(emptyText);
    }

    for (const msg of filtered) {
      const isOwn = msg.senderId === this.mmoState.getUserId() || msg.senderId === 'local';
      const nameColor = this.getUsernameColor(msg.senderId);
      const displayName = isOwn ? 'Me' : msg.senderName;

      const fullText = `${displayName}: ${msg.message}`;
      const msgText = this.scene.add.text(MSG_X, y, fullText, {
        fontSize: '13px',
        color: MSG_BODY_COLOR,
        fontFamily: Fonts.body,
        letterSpacing: 1,
        lineSpacing: 0,
        wordWrap: { width: MSG_W },
      });
      this.chatContentContainer.add(msgText);

      // Username overlay with correct color
      const nameOverlay = this.scene.add.text(MSG_X, y, `${displayName}:`, {
        fontSize: '13px',
        color: nameColor,
        fontFamily: Fonts.body,
        letterSpacing: 1,
      });
      this.chatContentContainer.add(nameOverlay);

      y += msgText.height + 11;
    }

    this.chatMaxScroll = Math.max(0, y - CONTENT_H);
  }

  private chatScrollToBottom(): void {
    this.chatScrollOffset = this.chatMaxScroll;
    this.chatContentContainer.setY(CONTENT_Y - this.chatScrollOffset);
  }

  // ── LOG display ─────────────────────────────────────────

  private addLogEntry(text: string, color: string): void {
    this.logEntries.push({ timestamp: Date.now(), text, color });
    if (this.logEntries.length > MAX_LOG_ENTRIES) this.logEntries.shift();
    this.refreshLogDisplay();
    this.logScrollToBottom();

    if (this.activeTab !== 'log') {
      this.logUnread++;
      this.buildTabs();
    }
  }

  private refreshLogDisplay(): void {
    this.logContentContainer.removeAll(true);
    const logContentH = PANEL_HEIGHT - CONTENT_Y - 8;

    let y = 0;
    for (const entry of this.logEntries) {
      const timeStr = this.formatLogTime(entry.timestamp);
      const text = this.scene.add.text(MSG_X, y, `${timeStr} ${entry.text}`, {
        fontSize: '11px',
        color: entry.color,
        fontFamily: Fonts.body,
        letterSpacing: 0.5,
        wordWrap: { width: MSG_W },
      });
      this.logContentContainer.add(text);
      y += text.height + 3;
    }

    this.logMaxScroll = Math.max(0, y - logContentH);
  }

  private logScrollToBottom(): void {
    this.logScrollOffset = this.logMaxScroll;
    this.logContentContainer.setY(CONTENT_Y - this.logScrollOffset);
  }

  private formatLogTime(ts: number): string {
    const d = new Date(ts);
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  // ── LOG event handlers ────────────────────────────────────

  private onLogCombatResult(result: any): void {
    if (!result || result.queued || !result.range) return;

    const myId = this.mmoState.getUserId();
    // Label ships relative to the player: "Your Scout" vs "Enemy Scout"
    const atkLabel = this.combatLabel(result.attackerId, result.attackerName, myId);
    const defLabel = this.combatLabel(result.defenderId, result.defenderName, myId);

    this.addLogEntry(
      `⚔️ ${atkLabel} vs ${defLabel} (${result.rangeBand})`,
      '#ffcc66',
    );

    // Attacker weapon breakdown
    for (const evt of (result.attackerEvents || [])) {
      if (evt.hit) {
        const shieldPart = evt.shieldDamage > 0 ? `shields -${evt.shieldDamage}` : '';
        const hullPart = evt.hullDamage > 0 ? `hull -${evt.hullDamage}` : '';
        const parts = [shieldPart, hullPart].filter(Boolean).join(', ');
        const breakdown = parts ? ` (${parts})` : '';
        this.addLogEntry(`  ${atkLabel}: ${evt.weaponUsed} → ${evt.damage} dmg${breakdown}`, '#ff6666');
        if (evt.subsystemHit) {
          const compLabel = evt.componentName || evt.subsystemHit.toUpperCase();
          this.addLogEntry(`  ⚠ ${compLabel} hit!`, '#ffaa00');
        }
      } else {
        this.addLogEntry(`  ${atkLabel}: ${evt.weaponUsed} → MISS`, '#888888');
      }
    }

    // Defender weapon breakdown
    for (const evt of (result.defenderEvents || [])) {
      if (evt.hit) {
        const shieldPart = evt.shieldDamage > 0 ? `shields -${evt.shieldDamage}` : '';
        const hullPart = evt.hullDamage > 0 ? `hull -${evt.hullDamage}` : '';
        const parts = [shieldPart, hullPart].filter(Boolean).join(', ');
        const breakdown = parts ? ` (${parts})` : '';
        this.addLogEntry(`  ${defLabel}: ${evt.weaponUsed} → ${evt.damage} dmg${breakdown}`, '#6666ff');
        if (evt.subsystemHit) {
          const compLabel = evt.componentName || evt.subsystemHit.toUpperCase();
          this.addLogEntry(`  ⚠ ${compLabel} hit!`, '#ffaa00');
        }
      } else {
        this.addLogEntry(`  ${defLabel}: ${evt.weaponUsed} → MISS`, '#888888');
      }
    }

    if (result.attackerDestroyed) this.addLogEntry(`  💥 ${atkLabel} DESTROYED`, '#ff0000');
    if (result.defenderDestroyed) this.addLogEntry(`  💥 ${defLabel} DESTROYED`, '#ff0000');
    if (!result.attackerDestroyed && !result.defenderDestroyed) {
      this.addLogEntry(`  Both survive (${result.attackerHp}HP / ${result.defenderHp}HP)`, '#66ff66');
    }
  }

  private combatLabel(unitId: string, unitName: string, myId: string | null): string {
    if (!myId) return unitName;
    // Look up unit owner from state manager, fall back to checking if userId is embedded in the unit ID
    const unit = this.mmoState.getUnit(unitId);
    const ownerId = unit?.ownerId;
    if (ownerId === myId || (!ownerId && unitId.includes(myId))) return `Your ${unitName}`;
    return `Enemy ${unitName}`;
  }

  private onLogEngagement(data: any): void {
    const name = data?.targetName || data?.targetId || 'unknown';
    this.addLogEntry(`⚔️ Engaged ${name}`, '#ff8844');
  }

  private onLogEngagementEnd(data: any): void {
    const name = data?.targetName || data?.targetId || 'target';
    this.addLogEntry(`⚔️ Combat with ${name} ended`, '#88aa66');
  }

  private onLogUnitMoved(data: any): void {
    if (!data) return;
    const name = data.unitName || data.unitType || 'Ship';
    const q = data.position?.q ?? data.q ?? '?';
    const r = data.position?.r ?? data.r ?? '?';
    this.addLogEntry(`→ ${name} moved to (${q}, ${r})`, '#88ccff');
  }

  private onLogUnitDocked(data: any): void {
    if (!data) return;
    const name = data.unitName || data.unitType || 'Ship';
    const loc = data.planetName || data.stationName || 'location';
    this.addLogEntry(`⚓ ${name} docked at ${loc}`, '#FFD700');
  }

  private onLogUnitUndocked(data: any): void {
    if (!data) return;
    const name = data.unitName || data.unitType || 'Ship';
    this.addLogEntry(`⚓ ${name} undocked`, '#FFD700');
  }

  private onLogBuildQueued(data: any): void {
    if (!data) return;
    const buildType = data.buildType || data.unitType || 'Unit';
    this.addLogEntry(`🔧 ${buildType} queued`, '#00ccaa');
  }

  private onLogBuildComplete(data: any): void {
    if (!data) return;
    const buildType = data.buildType || data.unitType || 'Unit';
    this.addLogEntry(`🔧 ${buildType} complete`, '#00FF88');
  }

  private onLogPurchase(data: any): void {
    if (!data) return;
    const item = data.itemType || data.unitType || 'item';
    const cost = data.cost || data.price;
    const costStr = cost ? ` for $${cost}` : '';
    this.addLogEntry(`💰 Purchased ${item}${costStr}`, '#00FF88');
  }

  private onLogAbility(data: any): void {
    if (!data) return;
    const ability = data.abilityName || data.name || 'Ability';
    const unit = data.unitName || 'Ship';
    this.addLogEntry(`⚡ ${unit} used ${ability}`, '#cc88ff');
  }

  private onLogMineResult(data: any): void {
    if (!data) return;

    // Handle new payload format: { minerals: Record<string, number>, asteroidDepleted: boolean }
    if (data.minerals && typeof data.minerals === 'object') {
      const mineralNames: Record<string, string> = {
        min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
        min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
        min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
      };
      const parts = Object.entries(data.minerals as Record<string, number>)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => `${mineralNames[id] || id} x${Number(qty).toFixed(1)}`)
        .join(', ');
      if (parts) {
        const suffix = data.asteroidDepleted ? ' (depleted!)' : '';
        this.addLogEntry(`⛏️ Mined ${parts}${suffix}`, '#FFAA00');
      }
      return;
    }

    // Legacy fallback
    const mineral = data.mineralType || data.mineral || 'minerals';
    const amount = data.amount || data.yield || 0;
    if (amount > 0) {
      this.addLogEntry(`⛏️ Mined ${amount}x ${mineral}`, '#FFAA00');
    }
  }

  private onLogCargoUnloaded(data: any): void {
    if (!data) return;
    const mineralNames: Record<string, string> = {
      min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
      min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
      min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
    };
    const parts: string[] = [];
    if (data.minerals) {
      for (const [id, qty] of Object.entries(data.minerals as Record<string, number>)) {
        if (qty > 0) parts.push(`${mineralNames[id] || id} x${Number(qty).toFixed(1)}`);
      }
    }
    if (data.stellarValue > 0) {
      parts.push(`$STELLAR ${Number(data.stellarValue).toFixed(2)}`);
    }
    if (parts.length > 0) {
      this.addLogEntry(`📦 Cargo unloaded: ${parts.join(', ')}`, '#00CCFF');
    }
  }

  private onLogTransferComplete(data: any): void {
    if (!data) return;
    const from = data.fromName || 'Unknown';
    const to = data.toName || 'Unknown';
    this.addLogEntry(`📦 Transfer complete: ${from} → ${to}`, '#00CCAA');
  }

  private onLogTransferStarted(data: any): void {
    if (!data) return;
    const from = data.fromName || 'Unknown';
    const to = data.toName || 'Unknown';
    const count = data.totalMinerals ? ` (${data.totalMinerals} minerals)` : '';
    this.addLogEntry(`📦 Transfer started: ${from} → ${to}${count}`, '#00AACC');
  }

  private onLogTransferCancelled(data: any): void {
    if (!data) return;
    const from = data.fromName || 'Unknown';
    const to = data.toName || 'Unknown';
    this.addLogEntry(`📦 Transfer cancelled: ${from} → ${to}`, '#CC8800');
  }

  private onLogMineralsDeposited(data: any): void {
    if (!data) return;
    const loc = data.locationName || 'Unknown';
    const summary = this.formatMineralSummary(data.minerals);
    this.addLogEntry(`📥 Deposited at ${loc}: ${summary}`, '#00CCFF');
  }

  private onLogMineralsWithdrawn(data: any): void {
    if (!data) return;
    const loc = data.locationName || 'Unknown';
    const summary = this.formatMineralSummary(data.minerals);
    this.addLogEntry(`📤 Withdrawn from ${loc}: ${summary}`, '#FFAA00');
  }

  private onLogCargoLoaded(data: any): void {
    if (!data) return;
    const mineral = data.mineralName || 'minerals';
    const amount = data.amount || 0;
    const ship = data.shipType || 'ship';
    this.addLogEntry(`📦 Loaded ${amount} ${mineral} onto ${ship}`, '#55CCAA');
  }

  private onLogStationWreckCreated(data: any): void {
    if (!data) return;
    const stationType = data.stationType || 'Station';
    const label = stationType.charAt(0).toUpperCase() + stationType.slice(1);
    const mineralCount = data.mineralCount ? ` (${data.mineralCount} minerals)` : '';
    const stellarCost = data.repairCostStellar ? ` — repair: $${Number(data.repairCostStellar).toFixed(2)}` : '';
    this.addLogEntry(`💥 ${label} station destroyed — wreck created${mineralCount}${stellarCost}`, '#FF4444');
  }

  private onLogStationWreckScavenged(data: any): void {
    if (!data) return;
    const stationType = data.stationType || 'Station';
    const label = stationType.charAt(0).toUpperCase() + stationType.slice(1);
    const mineral = data.mineralTypeId?.replace('min_', '') || 'mineral';
    const amount = data.amount || 1;
    this.addLogEntry(`🏴‍☠️ ${label} wreck scavenged — ${amount} ${mineral} stolen!`, '#FF8800');
  }

  private onLogStationRepaired(data: any): void {
    if (!data) return;
    const stationType = data.stationType || 'Station';
    const label = stationType.charAt(0).toUpperCase() + stationType.slice(1);
    this.addLogEntry(`🔧 ${label} station repaired and restored`, '#00FF88');
  }

  private formatMineralSummary(minerals: Record<string, number> | undefined): string {
    if (!minerals) return 'minerals';
    const names: Record<string, string> = {
      min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
      min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
      min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
    };
    const parts: string[] = [];
    for (const [id, qty] of Object.entries(minerals)) {
      if (qty > 0) parts.push(`${names[id] || id} x${Math.floor(qty)}`);
    }
    return parts.length > 0 ? parts.join(', ') : 'minerals';
  }

  // ── Scrolling ─────────────────────────────────────────────

  private setupScrolling(): void {
    const panelZone = this.scene.add.zone(0, CONTENT_Y, PANEL_WIDTH, CONTENT_H).setOrigin(0, 0);
    panelZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, PANEL_WIDTH, CONTENT_H), Phaser.Geom.Rectangle.Contains);
    this.add(panelZone);

    panelZone.on('wheel', (_pointer: any, _dx: number, deltaY: number) => {
      if (this.activeTab === 'chat') {
        this.chatScrollOffset = Phaser.Math.Clamp(this.chatScrollOffset + deltaY * 0.3, 0, this.chatMaxScroll);
        this.chatContentContainer.setY(CONTENT_Y - this.chatScrollOffset);
      } else {
        this.logScrollOffset = Phaser.Math.Clamp(this.logScrollOffset + deltaY * 0.3, 0, this.logMaxScroll);
        this.logContentContainer.setY(CONTENT_Y - this.logScrollOffset);
      }
    });
  }

  // ── Event handlers ────────────────────────────────────────

  private onAllianceChatMessage(data: { senderId: string; senderName: string; message: string }): void {
    // Skip own echo — we already added it locally in sendMessage()
    const myId = this.mmoState.getUserId();
    if (data.senderId === myId) return;

    this.chatMessages.push({
      id: `${Date.now()}-${Math.random()}`,
      senderId: data.senderId,
      senderName: data.senderName,
      message: data.message,
      timestamp: Date.now(),
      type: 'alliance',
    });
    if (this.chatMessages.length > MAX_CHAT_MESSAGES) this.chatMessages.splice(0, this.chatMessages.length - MAX_CHAT_MESSAGES);

    if (this.activeTab === 'chat') {
      this.refreshChatDisplay();
      this.chatScrollToBottom();
    } else {
      this.chatUnread++;
      this.buildTabs();
    }
    this.updateMobileLatestMsg();
  }

  private onGlobalChatMessage(data: any): void {
    const payload = data?.payload || data;
    // Skip own echo — we already added it locally in sendMessage()
    const myId = this.mmoState.getUserId();
    if ((payload.senderId || 'unknown') === myId) return;

    this.chatMessages.push({
      id: `${Date.now()}-${Math.random()}`,
      senderId: payload.senderId || 'unknown',
      senderName: payload.senderName || 'Unknown',
      message: payload.message || '',
      timestamp: payload.timestamp || Date.now(),
      type: 'global',
    });
    if (this.chatMessages.length > MAX_CHAT_MESSAGES) this.chatMessages.splice(0, this.chatMessages.length - MAX_CHAT_MESSAGES);

    if (this.activeTab === 'chat') {
      this.refreshChatDisplay();
      this.chatScrollToBottom();
    } else {
      this.chatUnread++;
      this.buildTabs();
    }
    this.updateMobileLatestMsg();
  }

  private updateMobileLatestMsg(): void {
    if (!this.mobileMode || !this.mobileLatestMsg || !this.mobileCollapsed) return;
    const last = this.chatMessages[this.chatMessages.length - 1];
    if (last) {
      const preview = `${last.senderName}: ${last.message}`;
      this.mobileLatestMsg.setText(preview.length > 50 ? preview.substring(0, 47) + '...' : preview);
    }
  }

  private onUserTyping(_data: { userId: string; username: string }): void {
    // Typing indicator could be added here
  }

  private onAllianceChanged(): void {
    // Rebuild channel bar when alliance status changes
    this.buildChannelBar();
  }

  private onWorldState(payload: any): void {
    if (payload?.combatHistory && payload.combatHistory.length > 0) {
      for (const entry of payload.combatHistory) {
        this.logEntries.push({ timestamp: Date.now(), text: entry.text, color: entry.color });
      }
      this.logEntries.push({ timestamp: Date.now(), text: '--- Live activity below ---', color: CSSColors.textMuted });
      this.refreshLogDisplay();
      this.logScrollToBottom();
    }
    EventBus.off(MMOEvents.WORLD_STATE, this.onWorldState, this);
  }

  // ── Cleanup ───────────────────────────────────────────────

  public destroy(fromScene?: boolean): void {
    // Chat events
    EventBus.off(MMOEvents.ALLIANCE_CHAT, this.onAllianceChatMessage, this);
    EventBus.off(MMOEvents.GLOBAL_CHAT, this.onGlobalChatMessage, this);
    EventBus.off('mmo-typing', this.onUserTyping, this);
    EventBus.off('mmo-alliance-updated', this.onAllianceChanged, this);
    EventBus.off(MMOEvents.WORLD_STATE, this.onWorldState, this);
    // LOG events
    EventBus.off(MMOEvents.COMBAT_RESULT, this.onLogCombatResult, this);
    EventBus.off(MMOEvents.ENGAGEMENT_STARTED, this.onLogEngagement, this);
    EventBus.off(MMOEvents.ENGAGEMENT_ENDED, this.onLogEngagementEnd, this);
    EventBus.off(MMOEvents.UNIT_MOVED, this.onLogUnitMoved, this);
    EventBus.off('mmo-unit-docked', this.onLogUnitDocked, this);
    EventBus.off('mmo-unit-undocked', this.onLogUnitUndocked, this);
    EventBus.off(MMOEvents.BUILD_QUEUED, this.onLogBuildQueued, this);
    EventBus.off(MMOEvents.BUILD_COMPLETE, this.onLogBuildComplete, this);
    EventBus.off(MMOEvents.PURCHASE_CONFIRMED, this.onLogPurchase, this);
    EventBus.off(MMOEvents.ABILITY_ACTIVATED, this.onLogAbility, this);
    EventBus.off(MMOEvents.MINE_RESULT, this.onLogMineResult, this);
    EventBus.off('mmo-log-cargo-unloaded', this.onLogCargoUnloaded, this);
    EventBus.off(MMOEvents.TRANSFER_COMPLETE, this.onLogTransferComplete, this);
    EventBus.off(MMOEvents.TRANSFER_STARTED, this.onLogTransferStarted, this);
    EventBus.off(MMOEvents.TRANSFER_CANCELLED, this.onLogTransferCancelled, this);
    EventBus.off(MMOEvents.MINERALS_DEPOSITED, this.onLogMineralsDeposited, this);
    EventBus.off(MMOEvents.MINERALS_WITHDRAWN, this.onLogMineralsWithdrawn, this);
    EventBus.off(MMOEvents.CARGO_LOADED, this.onLogCargoLoaded, this);
    EventBus.off(MMOEvents.STATION_WRECK_CREATED, this.onLogStationWreckCreated, this);
    EventBus.off(MMOEvents.STATION_WRECK_SCAVENGED, this.onLogStationWreckScavenged, this);
    EventBus.off(MMOEvents.STATION_REPAIRED, this.onLogStationRepaired, this);
    // Clean up hidden DOM input
    if (this.hiddenInput?.parentNode) {
      this.hiddenInput.parentNode.removeChild(this.hiddenInput);
    }
    // Detach any remaining visualViewport listeners (mobile keyboard lift)
    this.detachKeyboardLift();
    // Destroy detached clip mask graphics
    if (this.clipShape) {
      this.clipShape.destroy();
    }
    super.destroy(fromScene);
  }
}
